<template>
    <div data-component="ItemPanel"
         class="item-panel"
         :class="{'item-panel--searching': !!searchQuery}">
        <!-- On mobile collapse the category list into a dropdown to save horizontal space. -->
        <CategoryNavDropdown v-if="!searchQuery"
                             v-model="selectedCategory"
                             :categories="allCategories"
                             analytics-id-prefix="Item Panel"
                             class="mobile-only item-nav-dropdown" />

        <div class="item-nav non-mobile-only">
            <ul>
                <li v-for="category in allCategories"
                    :key="category.ID"
                    :class="{ active: selectedCategory?.ID === category.ID }">
                    <a @click.prevent="selectCategory(category)">
                        <i v-if="category.ID === FAVOURITE_CAT_ID"
                           class="fa-solid fa-star" />
                        <span class="category-name">{{ category.name }}</span>
                        <i class="fa-solid fa-chevron-right desktop-only"></i>
                    </a>
                </li>
            </ul>
        </div>

        <div class="item-buttons item-panel__right">
            <NoResults v-if="visibleEventButtons.length < 1 && visibleItems.length < 1"
                       icon="fa-solid fa-magnifying-glass"
                       :text="noResultsText"></NoResults>
            <div>
                <template v-for="itemCollection in visibleCategoryItemLists"
                          :key="itemCollection.category.ID">
                    <h4 v-if="searchQuery"
                        class="item-buttons__category-name">
                        {{ itemCollection.category.name }}
                    </h4>

                    <EventButton v-for="eventButton in itemCollection.eventButtons"
                                 :key="`event-button-${eventButton.event.ID}`"
                                 :event="eventButton.event"
                                 :highlight-items="searchQuery ? eventButton.matchingItems : null"
                                 :item-count="eventButton.items.length"
                                 @click="eventClicked(eventButton.event, itemCollection.category)" />

                    <ItemButton v-for="item in itemCollection.items"
                                :key="`item-button-${item.ID}`"
                                :item="item"
                                @click="itemClicked(item)" />
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import Category from '@/lib/Categories/Category';
import CategoryItemList from '@/lib/Items/CategoryItemList';
import CategoryNav from '@/epos/vue/components/Generic/CategoryNav';
import CategoryNavDropdown from '@/epos/vue/components/Generic/CategoryNavDropdown';
import EventButton from '@/epos/vue/pages/sell/components/EventButton';
import ItemButton from '@/epos/vue/pages/sell/components/ItemButton';
import NoResults from '@/vue/components/NoResults';
import { container } from '@/container';
import { sortByNavOrder } from '@/functions/arraySorting';
import { toUpperSnakeCase } from '@/functions/strings';

const { FAVOURITE_CAT_ID } = CategoryNav;

export default {
    components: { CategoryNavDropdown, EventButton, ItemButton, NoResults },

    props: {
        /**
         * @type {CategoryItemListCollection}
         */
        itemListCollection: {
            type: Object,
            required: true,
        },

        /**
         * @type {ItemType}
         */
        itemType: {
            type: String,
            required: true,
        },

        /**
         * @type {string}
         */
        searchQuery: {
            type: String,
            default: '',
        },
    },

    emits: [
        'eventClicked',
        'itemClicked',
    ],

    data() {
        return {
            FAVOURITE_CAT_ID,
            /**
             * @type {Category|null}
             */
            selectedCategory: null,
        };
    },

    computed: {
        /**
         * @return {number[]}
         */
        favouriteItemIDs() {
            return container.currentDevice.getDevice()?.deviceGroup?.favouriteItemIDs ?? [];
        },

        /**
         * @return {CategoryItemList[]}
         */
        originalCategoryItemLists() {
            return Object.values(this.itemListCollection.categories);
        },

        favouriteEventButtons() {
            if (this.favouriteItemIDs.length) {
                const eventButtons = this.getEventButtons(this.originalCategoryItemLists, true);
                // If we have 0 or 1 event button then there is no reason to check for duplicated events.
                if (eventButtons.length < 2) {
                    return eventButtons;
                }
                // Merge items array for all similar event buttons from different categories.
                const originalEventButtons = {};
                eventButtons.forEach((eventBtn) => {
                    const eventID = eventBtn.event.ID;
                    const savedEvent = originalEventButtons[eventID];
                    if (savedEvent) {
                        originalEventButtons[eventID] = {
                            ...savedEvent,
                            items: [...savedEvent.items, ...eventBtn.items],
                        };
                        return;
                    }
                    originalEventButtons[eventID] = { ...eventBtn };
                }, {});
                return Object.values(originalEventButtons);
            }
            return [];
        },
        favouriteItems() {
            if (this.favouriteItemIDs.length) {
                return this.getItems(this.originalCategoryItemLists).filter(this.getIsFavouriteItem);
            }
            return [];
        },

        /**
         * @return {Category|null}
         */
        favouritesCat() {
            if (!this.favouriteEventButtons.length && !this.favouriteItems.length) {
                return null;
            }
            const cat = new Category({
                ID: FAVOURITE_CAT_ID,
                name: container.langService.getText('SELL.FAVOURITES'),
            });
            return cat;
        },

        /**
         * At this point, create new CategoryItemList which is a shallow copy of the original itemListCollection
         * provided via props, but with favourites category added (if there is at least one favourite item selected).
         * No reason to mutate original state which can lead to unpredictable results.
         *
         * @return {CategoryItemListCollection}
         */
        itemListCollectionExpanded() {
            if (this.favouritesCat) {
                const favouriteCatItemList = new CategoryItemList(
                    { ...this.favouritesCat },
                    this.favouriteEventButtons,
                    this.favouriteItems,
                );
                return {
                    ...this.itemListCollection,
                    categories: {
                        [this.favouritesCat.ID]: favouriteCatItemList,
                        ...this.itemListCollection.categories,
                    },
                };
            }
            return this.itemListCollection;
        },

        /**
         * @return {CategoryItemList[]}
         */
        allCategoryItemLists() {
            return Object.values(this.itemListCollectionExpanded.categories);
        },

        /**
         * @return {Category[]}
         */
        allCategories() {
            const categories = this.allCategoryItemLists
                .map(
                    /**
                     * @param {CategoryItemList} collection
                     * @return {Category}
                     */
                    (collection) => collection.category,
                );

            sortByNavOrder(categories);

            return categories;
        },
        /**
         * @return {number[]}
         */
        allCatIDs() {
            return this.allCategories.map((cat) => cat.ID);
        },

        /**
         * Display these items on the right.
         *
         * @return {CategoryItemList[]}
         */
        visibleCategoryItemLists() {
            // Show nothing if there's no category selected.
            if (!this.selectedCategory) {
                return [];
            }

            // If a search is being performed we're going to show multiple categories.
            // Show them sorted by navOrder.
            if (this.searchQuery) {
                // Use original itemListCollection because favourites should not appear when search applied.
                const categoryLists = this.originalCategoryItemLists;
                sortByNavOrder(categoryLists);
                return categoryLists;
            }

            // Otherwise show the single selected category.
            return [
                this.itemListCollectionExpanded.categories[this.selectedCategory.ID],
            ];
        },

        visibleEventButtons() {
            return this.getEventButtons(this.visibleCategoryItemLists);
        },
        visibleItems() {
            return this.getItems(this.visibleCategoryItemLists);
        },

        noResultsText() {
            return container.langService.getText(
                'SELL.NO_SEARCH_RESULTS',
                {
                    itemType: container.langService.getText('ITEM_TYPE.' + toUpperSnakeCase(this.itemType)).toLowerCase(),
                }
            );
        },
    },

    watch: {
        itemType: {
            handler() {
                if (this.selectedCategory && this.allCatIDs.includes(this.selectedCategory.ID)) {
                    return;
                }
                this.selectCategory(this.allCategories[0]);
            },
            immediate: true,
        },
    },

    methods: {
        /**
         * @param {Category} newCategory
         */
        selectCategory(newCategory) {
            this.selectedCategory = newCategory;
        },

        /**
         * @param {SessionEvent} event
         * @param {Category|null} category
         */
        eventClicked(event, category = null) {
            this.$emit('eventClicked', { event, category });
        },

        /**
         * @param {AbstractItem} item
         */
        itemClicked(item) {
            this.$emit('itemClicked', { item });
        },

        /**
         * The user pressed enter in the search box.
         * "Click" on the first event / item button visible.
         * Returns true if something happened.
         *
         * @returns {boolean}
         */
        onSearchEnterPressed() {
            if (!this.searchQuery) {
                // Only do something if there is a search query.
                // Otherwise, it would just click the first item.
                return false;
            }

            // Don't do anything if there are any modals open.
            if (this.$store.getters.activeModals) {
                return false;
            }

            if (!this.visibleCategoryItemLists || this.visibleCategoryItemLists.length < 1) {
                return false;
            }

            /**
             * @type {CategoryItemList}
             */
            const firstVisibleCategory = this.visibleCategoryItemLists[0];

            // Event buttons appear first, so if there are any event buttons click on the first one.
            if (firstVisibleCategory.eventButtons.length > 0) {
                this.eventClicked(
                    firstVisibleCategory.eventButtons[0].event,
                    firstVisibleCategory.eventButtons[0].matchingItems?.length > 0 ? firstVisibleCategory.eventButtons[0].matchingItems[0].category : null,
                );
                return true;
            }

            // Click on the first item button if there is one.
            if (firstVisibleCategory.items.length > 0) {
                this.itemClicked(firstVisibleCategory.items[0]);
                return true;
            }

            return false;
        },

        /**
         * Verify that item is selected as favourite.
         *
         * @param {AbstractItem} item
         *
         * @return boolean
         */
        getIsFavouriteItem(item) {
            return this.favouriteItemIDs.includes(item.ID);
        },

        /**
         * Return all event buttons with the ability to filter favourite items for each of the entities.
         *
         * @param {CategoryItemList[]} catItemLists
         * @param {boolean} withFavouriteItems
         *
         * @return {{navOrder: number, event: SessionEvent, items: AbstractItem[], matchingItems?: AbstractItem[]}[]}
         */
        getEventButtons(catItemLists = [], withFavouriteItems = false) {
            return catItemLists?.reduce((result, categoryItemList) => {
                if (withFavouriteItems) {
                    const eventButtonsWithFavouriteItems = categoryItemList.eventButtons
                        .map((eventBtn) => {
                            const favouriteItems = eventBtn.items.filter(this.getIsFavouriteItem);
                            return {
                                ...eventBtn,
                                items: favouriteItems,
                            };
                        })
                        // No reason to show event without favourite items for it.
                        .filter((eventBtn) => eventBtn.items.length);
                    return result.concat(eventButtonsWithFavouriteItems);
                }
                return result.concat(categoryItemList.eventButtons);
            }, []);
        },
        /**
         * Return all items of the given category item lists.
         *
         * @param {CategoryItemList[]} catItemLists
         *
         * @return {AbstractItem[]}
         */
        getItems(catItemLists = []) {
            return catItemLists.reduce((result, categoryItemList) => {
                return result.concat(categoryItemList.items);
            }, []);
        },
    },
};
</script>

<style lang="less">
@import "../../../../../less/variables";

.item-panel {
    flex-grow: 1;
    background: @dt-mid-blue;
    padding-top: 7.5px;
    display: flex;
    flex-direction: row;

    // Even though this element won't have scrollbars itself it needs overflow:auto so that its
    // flex children are limited in height and can have scrollbars
    // https://stackoverflow.com/questions/49662361/firefox-flex-how-to-prevent-flex-items-from-growing-too-tall-with-flex-directi
    overflow: auto;
}

.item-nav {
    background: @dt-mid-blue;
    padding: (@gutter-width - 1) 0 0 0;
    display: block;
    width: 30%;
    flex-shrink: 0;
    font-size: @font-size-medium;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
    transition: all 0.15s ease-out;

    ul {
        list-style: none;
        padding: 0;
        margin: 0;
    }

    li {
        margin: 0;
        padding: 0 0 0 @half-gutter-width;
    }

    a {
        color: @white;
        border-bottom: solid 1px @dt-pale-blue;
        margin: 0;
        padding: 15px;
        display: flex;
        gap: 0 5px;
        align-items: center;
        cursor: pointer;

        .category-name {
            flex-grow: 1;
        }

        i {
            color: @dt-pale-blue;
        }
    }

    &:last-child {
        a {
            border-bottom: 0;
        }
    }

    .active a {
        background: white;
        color: @dt-dark-blue;
        border-radius: 5px 0 0 5px;

        i {
            color: @dt-dark-blue;
        }
    }
}

.item-panel__right {
    background: @white;
    flex-grow: 1;
    overflow: auto;
    padding: @half-gutter-width;
    border-radius: 5px 0 0 0;
    transition: all 0.15s ease-out;
}

// The mobile category dropdown (CategoryNavDropdown). Hidden on desktop via .mobile-only.
.item-nav-dropdown {
    background: @dt-mid-blue;
    flex-shrink: 0;
    padding: @half-gutter-width;
}

.item-buttons {
    > div {
        display: flex;
        flex-wrap: wrap;
    }

    &__category-name {
        margin: 10px;
        padding: 0 0 10px 0;
        border-bottom: 1px @dt-light-grey solid;
        flex-basis: 100%;
        font-size: @font-size-medium;
        color: @dt-grey;
    }
}

.item-panel.item-panel--searching {
    .item-nav {
        margin-left: -30%;
    }

    .item-panel__right {
        border-top-left-radius: 0;
    }
}

.item-button {
    flex-grow: 0;
    flex-shrink: 0;
    flex-basis: ~"calc(33.3333% - @{gutter-width})";
    cursor: pointer;
    background: white;
    font-size: @font-size-medium;
    font-weight: normal;
    border: 1px solid @btn-default-border;
    margin: @half-gutter-width;
    color: @dt-dark-blue;
    box-shadow: 0 3px 8px -9px black;
    border-radius: @border-radius-base;
    text-decoration: none;
    text-align: center;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 80px;
    position: relative;
    // Contain the __name/__waiver-icon z-indexes below. Without a stacking context here __name's z-index escapes
    // and draws over page overlays such as an open .dropmenu.
    z-index: 0;

    &:hover {
        background-color: @white;
        color: @text-color;
    }

    &:active {
        background-color: @light-grey-alt;
        color: @text-color;
    }

    &--disabled {
        opacity: .65;
        color: @btn-disabled-color !important;
        background: @btn-disabled-bg !important;
        border-color: @btn-disabled-border !important;
    }

    &__name {
        padding: 10px;
        // Center the ticket/event name when an 'x ticket types' bar is present.
        flex-grow: 1;
        display: flex;
        align-items: center;
        justify-content: center;

        // Overlap any icons such as the waivers icon.
        position: relative;
        z-index: 1;

        // Improve legibility when overlapping icons
        text-shadow: 1px 1px 0 rgba(255,255,255,1);
    }

    &__footer {
        background: @btn-default-bg;
        font-size: @font-size-small;
        padding: 10px 6px;
        margin-top: auto;
    }

    &__label {
        background-color: @dark-grey-alt;
        color: @white;
        padding: 1px 2px;
        border-radius: 0.25em;
        font-size: 10px;
        margin: 0 2px;
        white-space: nowrap;
    }

    &__waiver-icon {
        background: @btn-disabled-border;
        position: absolute;
        bottom: 0;
        right: 0;
        z-index: 0;
        padding: 3px 2px 2px 5px;
        font-size: 12px;
        color: @white;
        border-top-left-radius: @border-radius-base;
    }
}

@media (max-width: @mobile-max) {
    .item-panel {
        flex-direction: column;
        padding-top: 2px;

        &__right {
            padding: @gutter-width-narrow;

            > div {
                gap: @gutter-width-narrow;
            }
        }
    }

    .item-button {
        font-size: 12px;
        flex-basis: 100%;
        margin: 0;
        min-height: 55px;
        text-align: left;
        padding: 8px 10px;

        &__name {
            justify-content: flex-start; // Left align content.
            flex-grow: 0;
            padding: 0;
            color: @text-color-mobile;
            font-size: @font-size-large;
            font-weight: bold;
        }

        // Show the ticket types as a plain second line under the name.
        &__footer {
            background: none;
            margin-top: 0;
            padding: 0;
            color: @dt-grey;
            font-size: @font-size-medium;
        }
    }
}
</style>
